import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Col, InputGroup, Dropdown } from 'react-bootstrap';
import { Plus, X } from "lucide-react";
import TimeRange from '../time-range/TimeRange';
import type { TimeRange as TimeRangeType } from '../../types/TimeRange';
import { AppointmentMode, type AppointmentConfiguration as AppointmentConfigurationType } from '../../types/AppointmentConfiguration';
import AppointmentConfigurationService from '../../services/appointmentConfigurationService';
import { ToastType, useToast } from '../toast/ToastProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppointmentTypesManager from './AppointmentTypesManager';
import DurationSelect from '../duration-select/DurationSelect';
import { UtilsService } from '../../services/utilsService';

const BookingPreferences: React.FC = () => {
    const { t, i18n } = useTranslation();
    const dayKeys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
    type DayKey = typeof dayKeys[number];
    const getLocalizedDay = (day: DayKey, t: (key: string) => string) => t(day.toLowerCase());
    const days = [...dayKeys];

    const [appointmentConfig, setAppointmentConfig] = useState<AppointmentConfigurationType | null>(null);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const isRtl = i18n.language === 'he';

    useEffect(() => {
        const fetchAppointmentConfig = async () => {
            const controller = new AbortController();

            try {
                const clinicId = sessionStorage.getItem('activeClinicId');

                if (!clinicId) {
                    showToast(t(`toastMessages.USER_NOT_CONNECTED_CLINIC`), ToastType.Warning);
                    return;
                }

                const response = await AppointmentConfigurationService.getAppointmentConfiguration(clinicId, controller.signal, navigate);

                if (response) {
                    setAppointmentConfig(response);
                } else {
                    setDefaultAppointmentConfig(clinicId);
                }
            } catch (error) {
                showToast(t(`toastMessages.ERROR_FETCHING_BOOKING_PREFERENCES`), ToastType.Error);
            }
        };

        fetchAppointmentConfig();

    }, [showToast]);

    const setDefaultAppointmentConfig = (clinicId: string) => {
        setAppointmentConfig({
            treatmentTitle: '',
            treatmentName: '',
            bookingLinkId: '',
            startDate: '',
            endDate: null,
            noEndDate: true,
            duration: 30,
            appointmentTypes: [], 
            appointmentMode: AppointmentMode.FixedDuration, 
            activeDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
            timeRanges: [{ day: "", fromTime: "08:00", toTime: "16:00" }],
            clinicId,
        });
    };

    const [lastAddedDay, setLastAddedDay] = useState<string | null>(null);
    const [lastRemovedDay, setLastRemovedDay] = useState<string | null>(null);

    useEffect(() => {
        if (!lastAddedDay || !appointmentConfig) return;

        handleAddTimeRange(lastAddedDay);
        setLastAddedDay(null); // Reset after update
    }, [lastAddedDay]);

    useEffect(() => {
        if (!lastRemovedDay || !appointmentConfig) return;

        removeTimeRangesForDay(lastRemovedDay);
        setLastRemovedDay(null); // Reset after update
    }, [lastRemovedDay]);

    const handleDayTimeRange = (day: string) => {
        const isDayAlreadyActive = appointmentConfig?.activeDays.includes(day);
        if (!isDayAlreadyActive) {
            setLastAddedDay(day); // If a new day added - add TimeRange for it in useEffect [lastAddedDay]
        } else {
            setLastRemovedDay(day); // If a day removed - remove the day from TimeRange by useEffect [lastRemovedDay]
        }
    }

    const handleDayToggle = (day: string) => {
        setAppointmentConfig((prev) => {

            if (!prev) { return prev };
            const newDays = prev.activeDays?.includes(day) ? prev.activeDays.filter((d) => d !== day) : [...prev.activeDays, day];
            return { ...prev, activeDays: newDays };
        });

        handleDayTimeRange(day);
    };

    
    const handleAddTimeRange = (day?: string) => {
        if (!appointmentConfig) { return; } // Ensure appointmentConfig exists
        const newTimeRange: TimeRangeType = { day: day ?? "", fromTime: "08:00", toTime: "16:00" };
        setAppointmentConfig({ ...appointmentConfig, timeRanges: [...appointmentConfig.timeRanges, newTimeRange] });
    };

    const handleDeleteTimeRange = (index: number) => {
        if (!appointmentConfig) { return; } // Ensure appointmentConfig exists
        const updatedTimeRanges = appointmentConfig.timeRanges.filter((_, i) => i !== index);
        setAppointmentConfig({ ...appointmentConfig, timeRanges: updatedTimeRanges });
    };

    const removeTimeRangesForDay = (day: string) => {
        if (!appointmentConfig) return;

        const updatedTimeRanges = appointmentConfig.timeRanges.filter(
            (tr) => tr.day !== day
        );

        setAppointmentConfig({
            ...appointmentConfig,
            timeRanges: updatedTimeRanges,
        });
    };

    const handleNoEndDateToggle = () => {
        setAppointmentConfig(prev => {
            if (!prev) return prev; // handle null case safely

            const updatedNoEndDate = !prev.noEndDate;
            return {
                ...prev,
                noEndDate: updatedNoEndDate,
                endDate: updatedNoEndDate ? '' : prev.endDate, // clear endDate if noEndDate is true
            };
        });
    };

    const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;

        setAppointmentConfig((prev: any) => {
            if (!prev) return prev;
            return {
                ...prev,
                duration: value === '' ? undefined : parseInt(value, 10),
            };
        });
    };

    const generateTimeOptions = (startTime: number) => {
        const times: string[] = [];
        for (let h = startTime; h < startTime + 24; h++) {
            const adjustedHour = h % 24;
            for (let m = 0; m < 60; m += 15) {
                const hour = adjustedHour.toString().padStart(2, "0");
                const minute = m.toString().padStart(2, "0");
                times.push(`${hour}:${minute}`);
            }
        }
        return times;
    };

    const generateTimeOptionsObjects = (startTime: number) => {
        return generateTimeOptions(startTime).map((time) => ({
            value: time,
            label: time,
        })
        );
    };

    const generateActiveDaysOptions = (activeDays: string[]) => {
        return activeDays.map((day) => ({
            value: day,
            label: getLocalizedDay(day as DayKey, t),
        }));
    };

    const dayOptions = generateActiveDaysOptions(appointmentConfig?.activeDays || []);
    const timeOptions = generateTimeOptionsObjects(7);

    const handleRangeChange = (index: number, newTimeRange: TimeRangeType) => {
        if (!appointmentConfig) { return; } // Ensure appointmentConfig exists
        const updatedTimeRanges = [...appointmentConfig.timeRanges];
        updatedTimeRanges[index] = newTimeRange;
        setAppointmentConfig({ ...appointmentConfig, timeRanges: updatedTimeRanges });
    };

    const handleSaveAppointmentConfiguration = async () => {
        const controller = new AbortController();
        const clinicId = sessionStorage.getItem('activeClinicId');

        if (!appointmentConfig) {
            showToast(t(`toastMessages.BOOKING_PREFERENCES_NOT_LOADED`), ToastType.Warning);
            return;
        }

        if (appointmentConfig?.appointmentMode === AppointmentMode.Types && !appointmentConfig?.appointmentTypes?.length) {
            showToast(t(`toastMessages.ADD_AT_LEAST_ONE_APPOINTMENT_TYPE`), ToastType.Warning);
            return;
        }

        if(!isValidTimeRanges(appointmentConfig?.timeRanges)){
            showToast(t(`toastMessages.SELECT_DAY_AND_VERIFY_TIME`), ToastType.Warning);
            return;
        }

        // Create a fully defined configuration object, ensuring required fields are present
        const configToSave: AppointmentConfigurationType = {
            ...appointmentConfig,
            clinicId,
            startDate: appointmentConfig.startDate || new Date().toISOString(),
            endDate: appointmentConfig.endDate || null, // or keep as null if no value
            noEndDate: appointmentConfig.noEndDate ?? false,
            duration: appointmentConfig.duration ?? 5,
            appointmentTypes: appointmentConfig.appointmentTypes || null,
            appointmentMode: appointmentConfig.appointmentMode || AppointmentMode.FixedDuration,
            activeDays: appointmentConfig.activeDays || [t('mon'), t('tue'), t('wed'), t('thu'), t('fri')],
            timeRanges: appointmentConfig.timeRanges || [{ day: "", fromTime: "08:00", toTime: "16:00" }],
            treatmentTitle: appointmentConfig.treatmentTitle || null,
            treatmentName: appointmentConfig.treatmentName || null,
        };

        try {
            const newAppointmentConfig = await AppointmentConfigurationService.createAppointmentConfiguration(configToSave, controller.signal);

            if (newAppointmentConfig?.error) {
                showToast(newAppointmentConfig.message, ToastType.Warning);
                return;
            }

            showToast(t(`toastMessages.BOOKING_PREFERENCES_SAVED`), ToastType.Success);
        } catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to save Booking Preferences', ToastType.Error);
        }
    };

    const isValidTimeRanges = (timeRanges: TimeRangeType[]): boolean => {
        return timeRanges.every((item) => {
            const hasValues = item.day.trim() !== '';

            if (!hasValues) return false;

            // Compare times
            const from = UtilsService.parseTimeToMinutes(item.fromTime);
            const to = UtilsService.parseTimeToMinutes(item.toTime);

            return to > from; // toTime must be later than fromTime
        });
    };

    return (
        <div className="bg-dark text-light p-4 rounded">
            <h3 className="mb-3">{t('booking-preferences')}</h3>
            <p className="text-muted mb-4">{t('booking-preferences-title')}</p>

            <h5 className="mb-3">{t('treatment')}</h5>
            <Row className="mb-4">
                {/* Clinic Name */}
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="text-secondary">{t('treatment-title')}</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            className="input-bg-dark text-white border-0 shadow-sm"
                            placeholder={t('enter-treatment-title')}
                            value={appointmentConfig?.treatmentTitle ?? ''}
                            onChange={(e) =>
                                setAppointmentConfig((prev) =>
                                    prev ? { ...prev, treatmentTitle: e.target.value } : prev
                                )
                            }
                        />
                    </InputGroup>
                </Col>

                {/* Treatment Name */}
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="text-secondary">{t('treatment-name')}</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            className="input-bg-dark text-white border-0 shadow-sm"
                            placeholder={t('enter-treatment-name')}
                            value={appointmentConfig?.treatmentName ?? ''}
                            onChange={(e) =>
                                setAppointmentConfig((prev) =>
                                    prev ? { ...prev, treatmentName: e.target.value } : prev
                                )
                            }
                        />
                    </InputGroup>
                </Col>

                {/* Booking Link */}
                <Col xs={12} md={4}>
                    <Form.Label className="text-secondary">{t('booking-link')}</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            className="input-bg-dark text-white border-0 shadow-sm"
                            placeholder=""
                            value={
                                appointmentConfig?.bookingLinkId
                                    ? `${window.location.origin}/${i18n.language}/booking/${appointmentConfig.bookingLinkId}`
                                    : ''
                            }
                            readOnly
                            style={{ backgroundColor: '#2A3038' }}
                        />
                        <Button
                            variant="outline-light"
                            onClick={() => {
                                if (appointmentConfig?.bookingLinkId) {
                                    navigator.clipboard.writeText(
                                        `${window.location.origin}/${i18n.language}/booking/${appointmentConfig.bookingLinkId}`
                                    );
                                }
                            }}
                        >
                            {t('copy')}
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            <h5 className="mb-3">{t('date-range')}</h5>
            <Row className="mb-4">
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="text-secondary">{t('start-date')}</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="date"
                            className="input-bg-dark text-white border-0 shadow-sm"
                            value={appointmentConfig?.startDate ?? ''}
                            onChange={(e) =>
                                setAppointmentConfig((prev) =>
                                    prev ? { ...prev, startDate: e.target.value } : prev
                                )
                            }
                        />
                    </InputGroup>
                </Col>

                <Col xs={12} md={4} className="mb-3 mb-md-0">
                    <Form.Label className="text-secondary">{t('end-date')}</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="date"
                            className="input-bg-dark text-white border-0 shadow-sm"
                            value={appointmentConfig?.endDate ?? ''}
                            onChange={(e) =>
                                setAppointmentConfig((prev) =>
                                    prev ? { ...prev, endDate: e.target.value } : prev
                                )
                            }

                            disabled={appointmentConfig?.noEndDate}
                        />
                    </InputGroup>
                </Col>

                <Col xs={12} md={4} className="d-flex align-items-end">
                    <Form.Check
                        type="checkbox"
                        id="unlimited-checkbox"
                        className={`mt-2 text-gray d-flex align-items-center ${isRtl ? 'me-2' : 'ms-2'}`}
                        label={<span className="text-gray" style={{ verticalAlign: 'sub' }}>{t('set-as-unlimited')}</span>}
                        checked={appointmentConfig?.noEndDate ?? false}
                        onChange={handleNoEndDateToggle}
                    />
                </Col>
            </Row>

            <hr className="bg-secondary" />

            {/* Toggle Mode */}
            <Form.Group className="mb-4">
                <Form.Check
                    type="radio"
                    name="appointmentMode"
                    id="durationMode"
                    className="mb-3 d-flex align-items-center"
                >
                    <Form.Check.Input
                        type="radio"
                        name="appointmentMode"
                        id="fixedDurationRadio"
                        checked={appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration}
                        onChange={() => {
                            setAppointmentConfig(prev => prev ? { ...prev, appointmentMode: AppointmentMode.FixedDuration } : prev);
                        }}
                    />

                    <Form.Check.Label
                        htmlFor="fixedDurationRadio"
                        className={`${isRtl ? 'mr-2' : 'ms-2'}`}
                    >
                        {t('appointment-duration')} - {t('appointment-fixed-duration-explanation')}
                    </Form.Check.Label>
                </Form.Check>

                <Form.Check
                    type="radio"
                    name="appointmentMode"
                    id="typesMode"
                    className="mb-3 d-flex align-items-center"
                >
                    <Form.Check.Input
                        type="radio"
                        name="appointmentMode"
                        id="typesRadio"
                        checked={appointmentConfig?.appointmentMode === AppointmentMode.Types}
                        onChange={() => {
                            setAppointmentConfig(prev => prev ? { ...prev, appointmentMode: AppointmentMode.Types } : prev);
                        }}
                    />
                    <Form.Check.Label
                        htmlFor="typesRadio"
                        className={`${isRtl ? 'mr-2' : 'ms-2'}`}
                    >
                        {t('appointment-types')} - {t('appointment-types-explanation')}
                    </Form.Check.Label>
                </Form.Check>
            </Form.Group>

            {/* Appointment Duration */}
            {appointmentConfig?.appointmentMode === AppointmentMode.FixedDuration && (
                <>
                    <h5 className="mb-3">{t('appointment-duration')}</h5>
                    <Row className="mb-4">
                        <Col>
                            <div className="d-flex">
                                <DurationSelect
                                    value={appointmentConfig?.duration ?? ''}
                                    onChange={handleDurationChange}
                                    isRtl={isRtl}
                                    t={t}
                                    style={{ maxWidth: '215px' }}
                                />
                            </div>
                        </Col>
                    </Row>
                </>
            )}

            {/* Appointment Types Manager */}
            {appointmentConfig?.appointmentMode === AppointmentMode.Types && (
                <AppointmentTypesManager
                    clinicId={appointmentConfig?.clinicId ?? ''}
                    initialTypes={appointmentConfig?.appointmentTypes || []}
                    onChange={(updatedTypes) => {
                        setAppointmentConfig((prev) =>
                            prev ? { ...prev, appointmentTypes: updatedTypes } : prev
                        );
                    }}
                />
            )}

            <hr className="bg-secondary" />
            {/* Day Activity */}
            <h5 className="mb-3">{t('day-activity')}</h5>
            <Row className="mb-4">
                <Col>
                    <div className="days-selector mb-4">
                        <div className="d-flex flex-wrap gap-2">
                            {days.map((dayKey) => {
                                const translatedDay = getLocalizedDay(dayKey as DayKey, t);
                                const isActive = appointmentConfig?.activeDays?.includes(dayKey);

                                return (
                                    <button
                                        key={dayKey}
                                        type="button"
                                        className={`btn day-btn ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handleDayToggle(dayKey)} // also send the key
                                    >
                                        {translatedDay}
                                    </button>
                                );
                            })}
                        </div>

                    </div>
                </Col>
            </Row>

            {/* Available Hours */}
            <h5 className="mb-3">{t('available-hours')}</h5>
            {appointmentConfig?.timeRanges?.map((range, index) => (
                <div key={index} >
                    <TimeRange
                        timeOptions={timeOptions}
                        fromTime={range.fromTime}
                        toTime={range.toTime}
                        onFromTimeChange={(fromTime) => handleRangeChange(index, { day: range.day, fromTime, toTime: range.toTime })}
                        onToTimeChange={(toTime) => handleRangeChange(index, { day: range.day, fromTime: range.fromTime, toTime })}
                        dayOptions={dayOptions}
                        day={range.day}
                        onDayChange={(day) => handleRangeChange(index, { day, fromTime: range.fromTime, toTime: range.toTime })}
                    >
                        
                        {/* This will be rendered inline with the time dropdowns */}
                        {index !== 0 && (
                            <Button variant="danger" size="sm" onClick={() => handleDeleteTimeRange(index)}>
                                <i className="mdi mdi-delete-variant btn-icon-prepend"></i> {t('delete')}
                            </Button>
                        )}
                    </TimeRange>
                </div>
            ))}

            <Row>
                <Col xs={12} md={1}>
                    <Button variant="secondary" className="p-2" onClick={() => handleAddTimeRange()}>
                        <Plus size={20} />
                    </Button>
                </Col>
            </Row>
            <div className="d-flex justify-content-end mt-4">
                <Button variant="success" onClick={handleSaveAppointmentConfiguration}>
                    {t('save-configuration')}
                </Button>
            </div>
        </div>
    );
};

export default BookingPreferences;
